import type { Map } from "ol";
import Overlay from "ol/Overlay";
import { fromLonLat } from "ol/proj";
import { buildLocationInfoOverlay } from "./overlayVariants/locationInfoOverlay";
import { buildRoadCameraInfoOverlay } from "./overlayVariants/roadCameraInfoOverlay";
import { buildTransportInfoOverlay } from "./overlayVariants/transportInfoOverlay";

export type MapOverlayType = "locationInfo" | "roadCameraInfo" | "transportInfo" | "html";

export type MapOverlayPosition = {
    longitude: number;
    latitude: number;
};

export type LocationInfoOverlayData = {
    title?: string;
    latitude: number;
    longitude: number;
    road?: string;
    path?: string;
    maintenancePosition?: string;
    kmM?: string;
};

export type RoadCameraInfoOverlayData = {
    fields: {
        label: string;
        value: string;
    }[];
};

export type TransportInfoOverlayData = {
    title?: string;
    fields: {
        label: string;
        value: string;
    }[];
};

export type MapOverlayItem =
    {
        id?: string | number;
        type: "locationInfo";
        position: MapOverlayPosition;
        data?: LocationInfoOverlayData;
    } |
    {
        id?: string | number;
        type: "roadCameraInfo";
        position: MapOverlayPosition;
        data?: RoadCameraInfoOverlayData;
    } |
    {
        id?: string | number;
        type: "transportInfo";
        position: MapOverlayPosition;
        data?: TransportInfoOverlayData;
    };

type OverlayPlacement = {
    className: string;
    offset: [number, number];
    positioning:
    | "bottom-left"
    | "bottom-center"
    | "bottom-right"
    | "top-left"
    | "top-center"
    | "top-right";
};

function getOverlayPlacement(map: Map, coordinates: number[], overlay: Overlay): OverlayPlacement {
    const element = overlay.getElement() as HTMLElement | undefined;
    const mapSize = map.getSize();
    const pixel = map.getPixelFromCoordinate(coordinates);

    if (!element || !mapSize || !pixel) {
        return {
            className: "ol-popup--placement-bottom-center",
            offset: [0, -18],
            positioning: "bottom-center",
        };
    }

    const width = element.offsetWidth || 260;
    const height = element.offsetHeight || 140;
    const leftSpace = pixel[0];
    const rightSpace = mapSize[0] - pixel[0];
    const topSpace = pixel[1];
    const bottomSpace = mapSize[1] - pixel[1];

    const horizontal = leftSpace < width / 2 && rightSpace > leftSpace
        ? "left"
        : rightSpace < width / 2 && leftSpace > rightSpace
            ? "right"
            : "center";

    const vertical = topSpace >= height + 24 || topSpace >= bottomSpace
        ? "bottom"
        : "top";

    const positioning = `${vertical}-${horizontal}` as OverlayPlacement["positioning"];

    const offsetX = horizontal === "left" ? 12 : horizontal === "right" ? -12 : 0;
    const offsetY = vertical === "bottom" ? -18 : 18;

    return {
        className: `ol-popup--placement-${positioning}`,
        offset: [offsetX, offsetY],
        positioning,
    };
}

function applyOverlayPlacement(map: Map, coordinates: number[], overlay: Overlay) {
    requestAnimationFrame(() => {
        const element = overlay.getElement() as HTMLElement | undefined;
        if (!element) return;

        const placement = getOverlayPlacement(map, coordinates, overlay);
        const placementClasses = [
            "ol-popup--placement-bottom-left",
            "ol-popup--placement-bottom-center",
            "ol-popup--placement-bottom-right",
            "ol-popup--placement-top-left",
            "ol-popup--placement-top-center",
            "ol-popup--placement-top-right",
        ];

        element.classList.remove(...placementClasses);
        element.classList.add(placement.className);
        overlay.setPositioning(placement.positioning);
        overlay.setOffset(placement.offset);
        overlay.setPosition(coordinates);
    });
}

export function activateAddOverlaysFunction(overlaysList: MapOverlayItem[] = [], map?: Map) {
    const added: Overlay[] = [];

    if (!map || !Array.isArray(overlaysList) || overlaysList.length === 0) {
        return {
            off: () => {
                // nothing
            },
        };
    }

    overlaysList.forEach((overlayItem) => {
        try {
            const { position } = overlayItem as any;
            if (!position || typeof position.longitude !== "number" || typeof position.latitude !== "number") return;

            const coordinates = fromLonLat([position.longitude, position.latitude]);

            let overlay: Overlay | undefined;
            if (overlayItem.type === "locationInfo") {
                overlay = buildLocationInfoOverlay(overlayItem.id, overlayItem.data);
            }
            if (overlayItem.type === "roadCameraInfo") {
                overlay = buildRoadCameraInfoOverlay(overlayItem.id, overlayItem.data);
            }
            if (overlayItem.type === "transportInfo") {
                overlay = buildTransportInfoOverlay(overlayItem.data);
            }

            if (!overlay) return;

            overlay.setPosition(coordinates);
            map.addOverlay(overlay);
            applyOverlayPlacement(map, coordinates, overlay);
            added.push(overlay);
        } catch {
            
        }
    });

    const refreshPlacement = () => {
        added.forEach((overlay) => {
            const coordinates = overlay.getPosition();
            if (Array.isArray(coordinates)) {
                applyOverlayPlacement(map, coordinates, overlay);
            }
        });
    };

    map.on("moveend", refreshPlacement);
    map.on("change:size", refreshPlacement);

    return {
        off: () => {
            map.un("moveend", refreshPlacement);
            map.un("change:size", refreshPlacement);
            added.forEach((o) => map.removeOverlay(o));
        },
    };
}
